import { defineElement, FC, onDidLoad, onDidUnload, useElement, useEvent } from '@atomify/hooks';
import { inviewObserver } from '@source/utilities/inview';
import { useStylesheet } from '@utilities/hooks';

import style from './inview-container.scss';
import { InviewIndicatorElement } from './inview-indicator';

export const INVIEW_CLASS = 'is--in-view';
export const INVIEW_HOOK = 'js-hook-inview-item';

export interface InviewContainerElement extends HTMLElement {}

const InviewContainer: FC<InviewContainerElement> = ({ element }) => {
    let elements: NodeListOf<Element> | null;
    const inViewElementsChanged = useEvent({ eventName: 'change' });
    const inviewIndicator = useElement<InviewIndicatorElement>('inview-indicator');

    useStylesheet(style);

    onDidLoad(async () => {
        initObservers();
        await initInviewIndicator();
    });

    onDidUnload(() => {
        if (!elements) return;
        Array.from(elements).forEach((item) => {
            inviewObserver.unobserve(item);
        });
    });

    async function initInviewIndicator() {
        if (inviewIndicator.current) {
            await (inviewIndicator.current as any).componentOnReady();
            inviewIndicator.current.amount = elements?.length || 0;
        }
    }

    function initObservers() {
        const { root, threshold, rootDisabled } = element.dataset;
        elements = element.querySelectorAll(`[${element.dataset.inviewHook || INVIEW_HOOK}]`);
        const targetElement = !!root ? document.querySelector(root) : element;

        const defaultOptions = {
            threshold: threshold ? parseInt(threshold) : 0.95,
            root: !!rootDisabled ? null : targetElement,
        };

        if (!elements) return;

        Array.from(elements).forEach((item) => {
            item &&
                inviewObserver.observe(
                    item,
                    (inView, intersection) => setActiveElement(inView, intersection),
                    defaultOptions,
                );
        });
    }

    function setActiveElement(inView: boolean, intersection: IntersectionObserverEntry) {
        const { target } = intersection;
        const action = inView ? 'add' : 'remove';
        target.classList[action](INVIEW_CLASS);

        if (inviewIndicator.current && inView) {
            inviewIndicator.current.activeItem = Array.from(elements || []).indexOf(
                getLastInviewElement(),
            );
        }
        inViewElementsChanged.emit({ intersection, target, inView, elements: elements });
    }

    function getLastInviewElement() {
        const items = Array.from(elements || []).filter(
            (item) => item && item.classList.contains(INVIEW_CLASS),
        );
        return items.length > 0 ? items[items.length - 1] : items[0];
    }
};

InviewContainer.props = {
    elements: {
        type: Array,
    },
};

defineElement('inview-container', InviewContainer);
